import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Leaf } from "lucide-react";
import { api } from "../../api";

/** Standalone print-perfect page for a packing list. Opens in a new tab. */
export default function PackingList() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  useEffect(() => { api.get(`/admin/orders/${id}`).then((r) => setOrder(r.data)); }, [id]);

  if (!order) return <div className="p-6 text-sm text-[#5C665A]">Loading…</div>;

  return (
    <div className="min-h-screen bg-white text-[#1A1D1A]" data-testid="packing-list">
      <div className="max-w-2xl mx-auto px-8 py-10 print:py-0 print:px-4">
        <div className="flex items-start justify-between border-b border-[#E2E4E0] pb-4">
          <div className="db-h">
            <div className="flex items-center gap-2 text-[#2D6A4F]"><Leaf size={18} /> <span className="text-base font-bold">DocBlock Wholesale</span></div>
            <div className="text-2xl font-bold mt-2">Packing list</div>
            <div className="text-sm text-[#5C665A] mt-0.5">Order <span className="db-mono">{order.order_number}</span> · {new Date(order.created_at).toLocaleDateString()}</div>
          </div>
          <button onClick={() => window.print()} className="print:hidden inline-flex items-center gap-1 h-9 px-3 rounded-md bg-[#2D6A4F] hover:bg-[#1B4332] text-white text-sm" data-testid="packing-list-print"><Printer size={14} /> Print</button>
        </div>

        <div className="grid grid-cols-2 gap-6 mt-4 text-sm">
          <div>
            <div className="text-xs uppercase text-[#5C665A] db-h font-semibold">Ship to</div>
            <div className="mt-1">
              {order.shipping_address && (order.shipping_address.street_address || order.shipping_address.city) ? (
                <address className="not-italic">
                  <div className="font-medium">{order.shipping_address.name || `${order.buyer?.first_name || ""} ${order.buyer?.last_name || ""}`.trim()}</div>
                  {order.shipping_address.company && <div>{order.shipping_address.company}</div>}
                  <div>{order.shipping_address.street_address}</div>
                  {order.shipping_address.address_line2 && <div>{order.shipping_address.address_line2}</div>}
                  <div>{[order.shipping_address.postal_code, order.shipping_address.city].filter(Boolean).join(" ")}</div>
                  {order.shipping_address.country_code && <div>{order.shipping_address.country_code}</div>}
                  {order.shipping_address.phone && <div className="text-[#5C665A] text-xs mt-0.5">Phone: {order.shipping_address.phone}</div>}
                </address>
              ) : (
                <>
                  <div className="font-medium">{order.buyer?.first_name} {order.buyer?.last_name}</div>
                  <div>{order.buyer?.company || ""}</div>
                  <div className="text-[#5C665A]">{order.buyer_email}</div>
                  <div className="text-[#5C665A]">{order.buyer?.phone || ""}</div>
                </>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase text-[#5C665A] db-h font-semibold">References</div>
            <div className="mt-1 db-mono text-xs">
              <div>VAT: {order.buyer?.vat_id || "—"}</div>
              <div>Import licence: {order.buyer?.import_license ? (order.buyer.import_license_number || "yes") : "no"}</div>
              <div>Status: <span className="capitalize">{order.status}</span></div>
            </div>
          </div>
        </div>

        <table className="w-full mt-6 text-sm border-t border-[#E2E4E0]">
          <thead className="text-[#5C665A]">
            <tr>
              <th className="text-left py-2 db-h font-semibold">#</th>
              <th className="text-left py-2 db-h font-semibold">Product · Size</th>
              <th className="text-left py-2 db-h font-semibold">SKU</th>
              <th className="text-right py-2 db-h font-semibold">Qty</th>
            </tr>
          </thead>
          <tbody>
            {order.lines.map((l, i) => (
              <tr key={l.id} className="border-t border-[#E2E4E0]" data-testid={`packing-line-${i}`}>
                <td className="py-2 db-mono text-xs">{i + 1}</td>
                <td className="py-2">{l.product_name} · {l.size_name}</td>
                <td className="py-2 db-mono text-xs">{l.sku}</td>
                <td className="py-2 text-right db-mono font-semibold">{l.quantity}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-[#1A1D1A]">
              <td colSpan={3} className="py-2 text-right db-h font-semibold">Total items</td>
              <td className="py-2 text-right db-mono font-bold" data-testid="packing-total-items">{order.lines.reduce((s, l) => s + l.quantity, 0)}</td>
            </tr>
          </tfoot>
        </table>

        {order.note && (
          <div className="mt-6 pt-4 border-t border-[#E2E4E0] text-sm">
            <div className="text-xs uppercase text-[#5C665A] db-h font-semibold">Buyer note</div>
            <div>{order.note}</div>
          </div>
        )}

        <div className="mt-10 text-[10px] text-[#5C665A] print:mt-6">
          Prepared with care by DocBlock. EUR VAT-exclusive. Please verify the count on arrival.
        </div>
      </div>
      <style>{`@media print { body { background: white; } .print\\:hidden { display: none !important; } }`}</style>
    </div>
  );
}
